/* eslint-disable jsx-a11y/label-has-associated-control */
import { BiVideo } from 'react-icons/bi';
import { ReactElement, useState, useEffect, useCallback } from 'react';

import { FormHandles } from '@unform/core';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Input, InputRadio, Textarea } from '~/components';
import { useAuth } from '~/hooks/auth.context';
import { useLoading } from '~/hooks/loading';
import { IExercise, IExerciseRequiredForm } from '~/models/Exercise';
import { ExerciseService } from '~/services';

import * as S from './styles';

interface IEditExerciseFormProps {
  formName: string;
  formRef: React.RefObject<FormHandles>;
  handleSubmit: (data: IExerciseRequiredForm) => void;
  exerciseId?: string;
}

interface ICadasterParams {
  segmentName: string;
}

function EditExerciseForm({
  formName,
  formRef,
  handleSubmit,
  exerciseId,
}: IEditExerciseFormProps): ReactElement {
  const router = useHistory();

  const { setLoading } = useLoading();

  const { segmentName } = useParams<ICadasterParams>();
  const { user } = useAuth();

  const [exercise, setExercise] = useState<IExercise>();
  const [hasVideo, setHasVideo] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IExerciseRequiredForm>();
  const [videoDescription, setVideoDescription] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const youtubeRegex = new RegExp(
      '^(https?://)?(www.)?(youtube.com|youtu.be)/.+',
    );
    const vimeoRegex = new RegExp('(https?://)?(www.)?vimeo.com/.+');

    const oldValue = exercise?.video_url;
    const newValue = e.target.value;

    if (newValue !== oldValue) {
      if (youtubeRegex.test(newValue) || vimeoRegex.test(newValue)) {
        formRef.current?.setFieldValue('video_url', e.target.value);
      } else {
        formRef.current?.setFieldValue('video_url', oldValue);
      }
    } else {
      formRef.current?.setFieldValue('video_url', oldValue);
    }
  };

  const handleGetSegments = useCallback(async () => {
    try {
      setLoading(true);

      const response = await ExerciseService.getOneExercise(exerciseId!);
      setExercise(response.data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao carregar os dados!');
    }
  }, [exerciseId]);

  useEffect(() => {
    if (exerciseId) {
      handleGetSegments();
    }
  }, [handleGetSegments]);

  useEffect(() => {
    setInitialData({
      name: exercise?.name ?? '',
      muscle_segment: exercise?.muscle_segment ?? segmentName, // exercise quando retorna do servidor caso for editar // segmentName quando for criar
      professional_id: exercise?.professional_id, // exercise quando retorna do servidor caso for editar
      video_url: exercise?.video_url,
      video_description: exercise?.video_description,
    });
    setVideoDescription(exercise?.video_description as string);

    if (!hasVideo) {
      setHasVideo(!!exercise?.video_url);
      formRef.current?.setFieldValue('video_url', null);
      formRef.current?.setFieldValue('video_description', '');
    }

    formRef.current?.setFieldValue('muscle_segment', exercise?.muscle_segment);
  }, [exercise, formRef, hasVideo, segmentName]);

  return (
    <S.FormWrapper
      id={formName}
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={initialData}
      style={{ maxWidth: '320px' }}
    >
      <Input name="name" label="Nome" />
      <Input disabled name="muscle_segment" label="Segmento muscular" />
      <S.RadiosContainer>
        <label>Adicionar vídeo</label>
        <label>
          <input
            type="radio"
            checked={!hasVideo}
            onClick={() => setHasVideo(false)}
          />{' '}
          Não
        </label>
        <label>
          <input
            type="radio"
            checked={hasVideo}
            onClick={() => setHasVideo(true)}
          />{' '}
          Sim
        </label>
      </S.RadiosContainer>
      {hasVideo && (
        <>
          <div>
            <S.Label>
              <p>Link do vídeo</p>
              <BiVideo
                onClick={() => {
                  exercise?.video_url &&
                    router.push(`/ilab/exercises/edit/${exerciseId}/video`, {
                      videoData: exercise,
                    });
                }}
              />
            </S.Label>
            <Input
              name="video_url"
              placeholder="Link do youtube ou vimeo"
              onChange={handleInputChange}
            />
          </div>
          <Textarea
            name="video_description"
            label="Descrição"
            maxLength={1000}
            customDescriptionCount={videoDescription}
            onChange={(e) => setVideoDescription(e.target.value)}
          />
          {/* <p>{videoDescription?.length || 0} / 1000</p> */}
        </>
      )}
    </S.FormWrapper>
  );
}

EditExerciseForm.defaultProps = {
  exerciseId: '',
};

export default EditExerciseForm;
